<script>
  /* global agGrid */
  import GRID_LOCALE_CS from '../js/gridLocaleCs.js';
  import GRID_LOCALE_EN from '../js/gridLocaleEn.js';
  import { onMount } from 'svelte';

  export let value;
  export let team;
  export let name;
  export let lang = undefined;
  export let mode = undefined;

  let statisticsBattingElement;

  let gridOptions;

  function getOptions () {
    return {
      theme: 'legacy',
      defaultColDef: {
        cellDataType: 'number',
        type: 'numericColumn',
        width: 90,
        sortable: true,
        filter: true,
        filterParams: {
          maxNumConditions: 1,
          defaultJoinOperator: 'OR',
          filterOptions: ['equals', 'inRange', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual'],
        },
        floatingFilter: true,
      },
      columnDefs: [
        {
          headerName: lang !== 'en' ? 'Hráč' : 'Player',
          // eslint-disable-next-line no-magic-numbers
          openByDefault: window.innerWidth > 800,
          children: [
            {
              field: 'lastName',
              headerName: lang !== 'en' ? 'Příjmení' : 'Last name',
              width: 135,
              cellDataType: 'text',
              type: [],
              pinned: true,
              cellStyle: { fontWeight: 'bold' },
              filterParams: {
                maxNumConditions: 2,
                filterOptions: ['contains'],
              },
              floatingFilter: false,
            },
            {
              field: 'firstName',
              headerName: lang !== 'en' ? 'Jméno' : 'First name',
              width: 135,
              cellDataType: 'text',
              type: [],
              pinned: true,
              filterParams: {
                maxNumConditions: 2,
                filterOptions: ['contains'],
              },
              floatingFilter: false,
              columnGroupShow: 'open',
            },
            {
              field: 'team',
              headerName: lang !== 'en' ? 'Tým' : 'Team',
              cellDataType: 'text',
              type: [],
              pinned: true,
              filterParams: {
                maxNumConditions: 4,
                filterOptions: ['contains'],
              },
              floatingFilter: false,
              columnGroupShow: 'open',
              cellRenderer: params => {
                return `
                  <div class='team-cell'>
                    <img class='team-cell-flag' src='img/static/flag/${params.data.flag}.svg'/>
                    <div class='team-cell-label'> ${params.value.toUpperCase()} </div>
                  </div>
                `;
              },
            },
          ],
        },
        {
          field: 'pa',
          headerName: 'PA',
          headerTooltip: 'Plate appearance',
          cellStyle: { fontWeight: 'bold' },
        },
        {
          field: 'ab',
          headerName: 'AB',
          headerTooltip: 'At bat',
          cellStyle: { fontWeight: 'bold' },
        },
        {
          field: 'r',
          headerName: 'R',
          headerTooltip: 'Run',
          cellStyle: { fontWeight: 'bold' },
        },
        {
          field: 'h',
          headerName: 'H',
          headerTooltip: 'Hit',
          cellStyle: { fontWeight: 'bold' },
        },
        {
          field: '2b',
          headerName: '2B',
          headerTooltip: 'Double',
        },
        {
          field: '3b',
          headerName: '3B',
          headerTooltip: 'Triple',
        },
        {
          field: 'hr',
          headerName: 'HR',
          headerTooltip: 'Home run',
        },
        {
          field: 'gdp',
          headerName: 'GDP',
          headerTooltip: 'Grounded into double play',
        },
        {
          field: 'sh',
          headerName: 'SH',
          headerTooltip: 'Sacrifice bunt',
        },
        {
          field: 'sf',
          headerName: 'SF',
          headerTooltip: 'Sacrifice fly',
        },
        {
          field: 'bb',
          headerName: 'BB',
          headerTooltip: 'Walk',
          cellStyle: { fontWeight: 'bold' },
        },
        {
          field: 'hp',
          headerName: 'HP',
          headerTooltip: 'Hit by pitch',
        },
        {
          field: 'sb',
          headerName: 'SB',
          headerTooltip: 'Stolen base',
        },
        {
          field: 'cs',
          headerName: 'CS',
          headerTooltip: 'Caught stealing',
        },
        {
          field: 'k',
          headerName: 'K',
          headerTooltip: 'Strikeout',
          cellStyle: { fontWeight: 'bold' },
        },
        {
          field: 'rbi',
          headerName: 'RBI',
          headerTooltip: 'Runs batted in',
          cellStyle: { fontWeight: 'bold' },
        },
        {
          field: 'tb',
          headerName: 'TB',
          headerTooltip: 'Total bases',
        },
        {
          field: 'xbh',
          headerName: 'XBH',
          headerTooltip: 'Extra base hit',
        },
        {
          field: 'avg',
          headerName: 'AVG',
          headerTooltip: 'Batting average',
          valueFormatter: params => params.value.toFixed(3),
        },
        {
          field: 'obp',
          headerName: 'OBP',
          headerTooltip: 'On base percentage',
          valueFormatter: params => params.value.toFixed(3),
        },
        {
          field: 'slg',
          headerName: 'SLG',
          headerTooltip: 'Slugging percentage',
          valueFormatter: params => params.value.toFixed(3),
        },
        {
          field: 'ops',
          headerName: 'OPS',
          headerTooltip: 'On base plus slugging',
          valueFormatter: params => params.value.toFixed(3),
        },
        {
          flex: 1,
          filter: false,
          sortable: false,
          suppressMovable: true,
        },
      ],
      animateRows: true,
      accentedSort: true,
      tooltipShowDelay: 500,
      rowData: value,
      localeText: lang !== 'en' ? GRID_LOCALE_CS : GRID_LOCALE_EN,
      isExternalFilterPresent: () => team !== 'all' || name,
      doesExternalFilterPass: node => {
        if (team !== 'all' && node.data.team !== team) return false;

        if (name) {
          return `${node.data.lastName} ${node.data.firstName}`.toLowerCase().replace(/\s+/g, '').includes(name.toLowerCase().replace(/\s+/g, ''));
        }

        return true;
      },
    };
  }

  onMount(() => {
    gridOptions = getOptions();

    gridOptions.api = agGrid.createGrid(statisticsBattingElement, gridOptions);
  });

  $: if (gridOptions && lang) {
    gridOptions.api.destroy();
    gridOptions = getOptions();

    gridOptions.api = agGrid.createGrid(statisticsBattingElement, gridOptions);
  }

  $: filterChanged(team, name);

  function filterChanged () {
    if (gridOptions) gridOptions.api.onFilterChanged(team, name);
  }
</script>

<div class='statistics-batting ag-theme-balham' bind:this={statisticsBattingElement}
  class:ag-theme-balham-dark={mode !== 'light'}/>

<style>
  .statistics-batting {
    width: 100%;
    height: 100%;
  }

  :global(.team-cell) {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  :global(.team-cell-flag) {
    height: 16px;
  }

  :global(body.dark .team-cell-flag) {
    filter: brightness(88%);
  }

  :global(.ag-root-wrapper) {
    border-radius: var(--radius-lg);
  }
</style>
